const COLORS = [
  "#7A3AEA",
  "#5F1CD5",
  "#BF9AFF",
  "#FFCC00",
  "#F59E0B",
  "#E5D7FF",
];
const BURST_PARTICLES = 140;
const RAIN_PARTICLES = 160;
const RAIN_SPREAD_MS = 1800;
const DURATION_MS = 4500;
const FADE_MS = 900;
// Clamping long frames pauses the animation while the page is busy instead of
// skipping ahead, so the celebration isn't missed on a heavy page.
const MAX_FRAME_MS = 32;
const GRAVITY = 0.25;
const DRAG = 0.98;
const MAX_FALL_SPEED = 3.2;

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  rotation: number;
  spin: number;
  wobble: number;
  wobbleSpeed: number;
  startsAt: number;
}

function burstParticle(i: number, width: number, height: number): Particle {
  const fromLeft = i % 2 === 0;
  const angle = (fromLeft ? -65 : -115) + (Math.random() - 0.5) * 30;
  const speed = 17 + Math.random() * 11;
  return {
    x: fromLeft ? 0 : width,
    y: height,
    vx: Math.cos((angle * Math.PI) / 180) * speed,
    vy: Math.sin((angle * Math.PI) / 180) * speed,
    ...looks(i),
    startsAt: 0,
  };
}

function rainParticle(i: number, width: number): Particle {
  return {
    x: Math.random() * width,
    y: -20,
    vx: (Math.random() - 0.5) * 1.5,
    vy: 1 + Math.random() * 2,
    ...looks(i),
    startsAt: Math.random() * RAIN_SPREAD_MS,
  };
}

function looks(i: number) {
  return {
    size: 7 + Math.random() * 7,
    color: COLORS[i % COLORS.length],
    rotation: Math.random() * Math.PI,
    spin: (Math.random() - 0.5) * 0.25,
    wobble: Math.random() * Math.PI * 2,
    wobbleSpeed: 0.05 + Math.random() * 0.08,
  };
}

export function launchConfetti(): void {
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
  const canvas = document.createElement("canvas");
  const maybeContext = canvas.getContext("2d");
  if (!maybeContext) return;
  const context: CanvasRenderingContext2D = maybeContext;
  const ratio = window.devicePixelRatio || 1;
  const width = window.innerWidth;
  const height = window.innerHeight;
  canvas.width = width * ratio;
  canvas.height = height * ratio;
  canvas.setAttribute("aria-hidden", "true");
  Object.assign(canvas.style, {
    position: "fixed",
    inset: "0",
    width: "100%",
    height: "100%",
    zIndex: "70",
    pointerEvents: "none",
  });
  document.body.appendChild(canvas);
  context.scale(ratio, ratio);

  const particles = [
    ...Array.from({ length: BURST_PARTICLES }, (_, i) =>
      burstParticle(i, width, height),
    ),
    ...Array.from({ length: RAIN_PARTICLES }, (_, i) => rainParticle(i, width)),
  ];

  let elapsed = 0;
  let last = performance.now();
  function frame(now: number) {
    // The first frame's time can be a little before the start was taken.
    const step = Math.min(Math.max(now - last, 0), MAX_FRAME_MS);
    last = now;
    elapsed += step;
    const scale = step / 16.7;
    context.clearRect(0, 0, width, height);
    context.globalAlpha = Math.min(1, (DURATION_MS - elapsed) / FADE_MS);
    for (const p of particles) {
      if (elapsed < p.startsAt) continue;
      p.vx *= DRAG ** scale;
      p.vy = Math.min(p.vy * DRAG ** scale + GRAVITY * scale, MAX_FALL_SPEED);
      p.wobble += p.wobbleSpeed * scale;
      p.x += (p.vx + Math.sin(p.wobble) * 0.8) * scale;
      p.y += p.vy * scale;
      p.rotation += p.spin * scale;
      context.save();
      context.translate(p.x, p.y);
      context.rotate(p.rotation);
      context.fillStyle = p.color;
      // Squashing with the wobble makes pieces look like they flip.
      context.fillRect(
        -p.size / 2,
        -p.size / 4,
        p.size,
        (p.size / 2) * Math.cos(p.wobble),
      );
      context.restore();
    }
    if (elapsed < DURATION_MS) {
      requestAnimationFrame(frame);
    } else {
      canvas.remove();
    }
  }
  requestAnimationFrame(frame);
}
